import styled from "styled-components";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { colors, motion, radii, spacing, typography } from "../../theme-v2";

export interface SuggestedPromptChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Visible chip label, typically a short LLM prompt. */
  children: ReactNode;
  /** Optional leading icon (e.g. ✨ / arrow / topic glyph). */
  icon?: ReactNode;
}

/**
 * SuggestedPromptChip — a clickable chip used in LLM empty states to
 * offer the user starter prompts. Distinct from `<Tag>` (status badges,
 * not actionable) and `<Button>` (too visually dominant).
 *
 * Visual: pill-shaped outlined button with subtle hover tint.
 */
export const SuggestedPromptChip = ({
  children,
  icon,
  type = "button",
  ...rest
}: SuggestedPromptChipProps) => (
  <StyledChip type={type} {...rest}>
    {icon && <StyledIconSlot>{icon}</StyledIconSlot>}
    <span>{children}</span>
  </StyledChip>
);

const StyledChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm}px;
  padding: 10px ${spacing.md}px;
  border: 1.5px solid ${colors.accent.base};
  border-radius: ${radii.lg}px;
  background: ${colors.accent.tintSubtle};
  color: ${colors.accent.base};
  /* Explicit theme font — buttons don't pick up the page font on routes
     without a global font style (media-player), which fell back to serif. */
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  line-height: 1.45;
  cursor: pointer;
  text-align: center;
  transition: background ${motion.duration.fast} ${motion.easing.standard};

  &:hover:not(:disabled) {
    background: ${colors.accent.tint};
  }

  &:focus-visible {
    outline: 2px solid ${colors.accent.base};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;
